import { useCallback, useEffect, useState } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { confirmAction, copyText, haptic, notify, openExternal, useBackButton } from '../telegram'
import type { Booking } from '../types'
import { euro } from './Booking'

interface Props {
  onClose: () => void
  /** Выбрать другое время в том же клубе */
  onBook: (clubId: number) => void
}

const ACTIVE = ['requested', 'confirmed']

function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString(t().locale, { hour: '2-digit', minute: '2-digit' })
}

/** Мои заявки и брони (v0.35): статус, реквизиты для перевода, отмена. */
export default function MyBookings({ onClose, onBook }: Props) {
  const [items, setItems] = useState<Booking[] | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  useEffect(() => useBackButton(onClose), [onClose])
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setItems(await api.myBookings())
    } catch (e) {
      setItems([])
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function cancel(b: Booking) {
    const late = b.status === 'confirmed' && b.cancel_free_until !== null && Date.now() > new Date(b.cancel_free_until).getTime()
    const hours = Math.round((new Date(b.starts_at).getTime() - new Date(b.cancel_free_until ?? b.starts_at).getTime()) / 3600000)
    const ask = b.status === 'requested' ? t().bookWithdrawAsk : late ? t().bookCancelLateAsk(hours) : t().bookCancelAsk
    if (!(await confirmAction(ask))) return
    setBusy(b.id)
    try {
      const updated = await api.cancelBooking(b.id)
      setItems((list) => (list ?? []).map((x) => (x.id === updated.id ? updated : x)))
      haptic('light')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
      void load()
    } finally {
      setBusy(null)
    }
  }

  const now = Date.now()
  const upcoming = (items ?? []).filter((b) => ACTIVE.includes(b.status) && new Date(b.ends_at).getTime() > now)
  const past = (items ?? []).filter((b) => !upcoming.includes(b)).reverse()

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().bookMine}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">🎾 {t().bookMine}</div>
      </div>
      <div className="chat-list">
        {items === null && <p className="muted">{t().loading}</p>}
        {items?.length === 0 && <div className="card muted">{t().bookEmpty}</div>}
        {upcoming.map((b) => (
          <BookingCard key={b.id} b={b} busy={busy === b.id} onCancel={() => cancel(b)} />
        ))}
        {past.length > 0 && (
          <>
            <div className="day-head">{t().bookPast}</div>
            {past.map((b) => (
              <BookingCard key={b.id} b={b} onAgain={() => onBook(b.club_id)} />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <button
      className="book-copy"
      onClick={async () => {
        if (await copyText(value)) {
          haptic('light')
          notify(t().bookCopied)
        }
      }}
    >
      <span className="muted small">{label}</span>
      <span className="book-copy-v">{value}</span>
      <span aria-hidden>⧉</span>
    </button>
  )
}

function BookingCard({ b, busy, onCancel, onAgain }: { b: Booking; busy?: boolean; onCancel?: () => void; onAgain?: () => void }) {
  const active = ACTIVE.includes(b.status) && new Date(b.ends_at).getTime() > Date.now()
  const late = b.cancel_free_until !== null && Date.now() > new Date(b.cancel_free_until).getTime()
  const badge = b.status === 'confirmed' ? '' : b.status === 'requested' ? ' gold' : ' neutral'
  return (
    <div className={`card book-card${active ? '' : ' past'}`}>
      <div className="row between" style={{ gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="title">
            {formatWhen(b.starts_at)}–{timeOnly(b.ends_at)}
          </div>
          <div className="muted">
            {b.club_name} · {b.court_name}
            {b.amount != null && ` · ${euro(b.amount)}`}
          </div>
        </div>
        <span className={`badge${badge}`}>{t().bookStatus[b.status]}</span>
      </div>

      {b.status === 'requested' && b.reply_by && active && (
        <div className="muted small" style={{ marginTop: 6 }}>
          {t().bookWaitUntil(formatWhen(b.reply_by))}
        </div>
      )}
      {(b.cancelled_by || b.late_cancel || b.no_show) && (
        <div className="muted small" style={{ marginTop: 6 }}>
          {[b.cancelled_by && t().bookCancelledBy[b.cancelled_by], b.late_cancel && t().bookLate, b.no_show && t().bookNoShow]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}

      {b.status === 'confirmed' && active && b.payment && (
        <div className="book-pay">
          <div className="small" style={{ fontWeight: 700, marginBottom: 6 }}>
            💶 {t().bookPay}
            {b.payment.amount != null && ` · ${euro(b.payment.amount)}`}
            {b.price_note && <span className="muted"> ({b.price_note})</span>}
          </div>
          {b.payment.iban && <CopyRow label={t().bookIban} value={b.payment.iban} />}
          {b.payment.holder && <CopyRow label={t().bookHolder} value={b.payment.holder} />}
          <CopyRow label={t().bookConcept} value={b.payment.concept} />
        </div>
      )}

      {b.status === 'confirmed' && active && b.cancel_free_until && (
        <div className={`small${late ? ' book-late' : ' muted'}`} style={{ marginTop: 8 }}>
          {late ? t().bookLateNow : t().bookFreeUntil(formatWhen(b.cancel_free_until))}
        </div>
      )}

      {active && (
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          {b.maps_url && b.status === 'confirmed' && (
            <button className="ghost" style={{ flex: 1 }} onClick={() => openExternal(b.maps_url!)}>
              📍 {t().bookRoute}
            </button>
          )}
          {b.can_cancel && onCancel && (
            <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={onCancel}>
              {b.status === 'requested' ? t().bookWithdraw : t().bookCancel}
            </button>
          )}
        </div>
      )}
      {!active && onAgain && (b.status === 'declined' || b.status === 'expired' || b.cancelled_by === 'club' || b.cancelled_by === 'calendar') && (
        <button className="ghost" style={{ width: '100%', marginTop: 10 }} onClick={onAgain}>
          {t().bookAgain}
        </button>
      )}
    </div>
  )
}
